import moment from 'moment';
import {useEffect, useMemo, useState} from 'react';

import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder/legacy';
import {
  getOverviewAttemptScope,
  getOverviewDateScope,
  getOverviewSubjectScope,
  setOverviewAttemptScope,
  setOverviewDateScope,
  setOverviewSubjectScope
} from 'client/Classrooms/Classroom/Teacher/TeacherClassroomScope/overviewSubjectScopeBridge';
import {MEGA_CLASSES_SIZE} from 'client/constants';
import {alphaComparatorAscending} from 'lib/SortUtil';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {StudentsListCollectionModel} from 'resources/Student/StudentsListCollection.model';
import type {StudentsListCollection} from 'resources/Student/StudentsListCollection.model';

// legacy Mandark subject shape we read for the scope options
interface OverviewSubject {
  getId: () => string;
  getName: () => string;
  isAdaptive: () => boolean;
}

// minimal legacy classroom shape — read only what the Overview needs
interface OverviewClassroom {
  getSubjects: () => {toArray: () => OverviewSubject[]};
  getMeta: () => {getCountOfStudents: () => number};
}

export interface SubjectOption {
  value: string;
  label: string;
}

/** null = "Most Recent" (the backend `most_recent` flag); a number selects that attempt. */
export type AttemptScope = number | null;

export interface OverviewScope {
  /** empty = the class's full default subject set */
  subjectIds: string[];
  attemptNumber: AttemptScope;
  /**
   * Selected date-range option id — a preset label (e.g. 'Last month') or 'Custom'; startDate/endDate
   * hold the resolved range. Mirrors the legacy SelectableDateRange options (no all-time scope).
   */
  dateMode: string;
  startDate: number | null;
  endDate: number | null;
}

export interface OverviewTiles {
  activeStudents: number;
  inactiveStudents: number;
  questionsAnswered: number;
  pointsEarned: number;
  averageAccuracy: number;
  timeSpentMs: number;
}

export interface ClassOverview {
  isReady: boolean;
  /** The classroom (subject options + mega gate) has loaded — enough to render the scope bar. */
  classroomReady: boolean;
  /** A students query is in flight (initial load or a scope-change refetch). Lets the page show a
   *  spinner only in the data region, keeping the scope bar (and any open date picker) mounted. */
  isLoadingStudents: boolean;
  hasError: boolean;
  /** > MEGA_CLASSES_SIZE students — the roster is too large to render. */
  isMegaClass: boolean;
  /** no non-adaptive subjects — there is no overview data to show. */
  hasNoSubjects: boolean;
  students: StudentsListCollection | null;
  tiles: OverviewTiles;
  subjectOptions: SubjectOption[];
  scope: OverviewScope;
  setSubjectIds: (ids: string[]) => void;
  setAttemptNumber: (attemptNumber: AttemptScope) => void;
  setDateRange: (startDate: number | null, endDate: number | null) => void;
  setDateMode: (mode: string) => void;
  reload: () => void;
}

const EMPTY_TILES: OverviewTiles = {
  activeStudents: 0,
  inactiveStudents: 0,
  questionsAnswered: 0,
  pointsEarned: 0,
  averageAccuracy: 0,
  timeSpentMs: 0
};

// The scope selections (subject/attempt/date) this Overview recorded earlier in the session, so
// they survive remounts — e.g. opening a student page and coming back. Otherwise match the
// legacy default (TeacherClassroomScope.store#getDefaultDateRange): a Custom range of the last
// month, so the Overview opens scoped to recent activity rather than all-time.
const createInitialScope = (classroomId: string): OverviewScope => {
  const subjectIds = getOverviewSubjectScope(classroomId) ?? [];
  const attemptNumber = getOverviewAttemptScope(classroomId)?.attemptNumber ?? null;
  const recorded = getOverviewDateScope(classroomId);
  if (recorded) {
    return {
      subjectIds,
      attemptNumber,
      dateMode: recorded.dateMode,
      startDate: recorded.startDate,
      endDate: recorded.endDate
    };
  }
  return {
    subjectIds,
    attemptNumber,
    dateMode: 'Custom',
    startDate: moment().subtract(1, 'month').startOf('day').valueOf(),
    endDate: moment().endOf('day').valueOf()
  };
};

/**
 * Data + scope state for the v2 class Overview tab — a Flux-free port of the legacy
 * classroomV2Store overview queries. One students_v2 query (scoped by subject/attempt/date) feeds
 * both the six stat tiles and the mastery-bucketed roster. Each query drives its own loading/error
 * via getResourcePromise + a derived status key, so a scope change re-fetches with a loading state.
 */
export const useClassOverview = (classroomId: string): ClassOverview => {
  const [scope, setScope] = useState<OverviewScope>(() => createInitialScope(classroomId));
  const [classroomStatus, setClassroomStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The scope-key of the students query that has resolved (loaded) or rejected (failed).
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);

  // The classroom carries the subject options + the student-count (mega-class) gate.
  const classroomQuery = useMemo(
    () =>
      mandarkEndpoint(['classrooms_v1', classroomId])
        .defaultModel(ClassroomModelV1)
        .include('subjects_v2')
        .customQuery({with_meta: 'classroom_v1'})
        .customQuery({meta: {context: {classroom: {counts: true}}}}) as unknown as QueryBuilder,
    [classroomId]
  );

  useEffect(() => {
    let isStale = false;
    classroomQuery
      .getResourcePromise()
      .then(() => {
        if (!isStale) {
          setClassroomStatus('ready');
        }
      })
      .catch(() => {
        if (!isStale) {
          setClassroomStatus('error');
        }
      });
    return () => {
      isStale = true;
    };
  }, [classroomQuery]);

  // Bridged read: re-runs on state-tree emits so invalidations from reused flows (e.g. a settings
  // save) refetch and reflect here — a bare render read would be frozen by the React Compiler.
  const classroomResource = useFluxStore(sessionStore, () =>
    classroomQuery.isResourcePopulated() ? classroomQuery.getResource() : null
  );
  const classroom =
    classroomStatus === 'ready' && classroomResource
      ? (classroomResource as unknown as OverviewClassroom)
      : null;

  const allSubjects = useMemo(
    () => (classroom ? classroom.getSubjects().toArray() : []),
    [classroom]
  );
  const nonAdaptiveSubjects = useMemo(
    () => allSubjects.filter((s) => !s.isAdaptive()),
    [allSubjects]
  );
  const subjectOptions = useMemo<SubjectOption[]>(
    () =>
      nonAdaptiveSubjects
        .map((s) => ({value: s.getId(), label: s.getName()}))
        .sort((a, b) => alphaComparatorAscending(a.label, b.label)),
    [nonAdaptiveSubjects]
  );

  const isMegaClass =
    classroom != null && classroom.getMeta().getCountOfStudents() > MEGA_CLASSES_SIZE;
  const hasNoSubjects = classroom != null && nonAdaptiveSubjects.length === 0;
  const needsStudents = classroom != null && !isMegaClass && !hasNoSubjects;

  // Default to every classroom subject id (incl. adaptive — matches the legacy aggregate scope)
  // until the user narrows to one of the non-adaptive dropdown options.
  const effectiveSubjectIdsKey = (
    scope.subjectIds.length ? scope.subjectIds : allSubjects.map((s) => s.getId())
  ).join(',');

  // Every option (preset or Custom) resolves to a concrete range — there is no all-time scope.
  const hasValidRange =
    scope.startDate != null && scope.endDate != null && scope.startDate < scope.endDate;

  // Identifies the current students query; loaded/failed are tracked against it so a scope change
  // (new key) reverts to loading until the new fetch resolves.
  const queryKey = `${effectiveSubjectIdsKey}|${scope.attemptNumber ?? 'recent'}|${
    hasValidRange ? `${scope.startDate}-${scope.endDate}` : 'all'
  }`;

  const studentsQuery = useMemo(
    () =>
      mandarkEndpoint(['classrooms_v1', classroomId, 'students_v2'])
        // StudentsListCollectionModel is a factory; defaultModel invokes it with `new`. Cast to the
        // constructor shape the builder expects (same cast the legacy store uses).
        .defaultModel(StudentsListCollectionModel as unknown as new (...args: unknown[]) => unknown)
        .pageSize(250)
        .customQuery({with_meta: 'student_v2'})
        .meta({context: {subject: {ids: effectiveSubjectIdsKey}}})
        .meta({context: {start_date: scope.startDate, end_date: scope.endDate}}, hasValidRange)
        .meta(
          {context: {guess: {attempt_number: scope.attemptNumber}}},
          scope.attemptNumber != null
        )
        .meta(
          {context: {guess: {most_recent: true}}},
          scope.attemptNumber == null
        ) as unknown as QueryBuilder,
    [
      classroomId,
      effectiveSubjectIdsKey,
      scope.attemptNumber,
      scope.startDate,
      scope.endDate,
      hasValidRange
    ]
  );

  // Carry the subject scope into the legacy single-student page (see overviewSubjectScopeBridge):
  // clicking a student off the Overview should open on the same subject, not the page's own default.
  useEffect(() => {
    setOverviewSubjectScope(classroomId, scope.subjectIds);
  }, [classroomId, scope.subjectIds]);

  // Same bridge for the attempt number.
  useEffect(() => {
    setOverviewAttemptScope(classroomId, scope.attemptNumber);
  }, [classroomId, scope.attemptNumber]);

  // Same bridge for the date range: the single-student page inherits the Overview's range instead
  // of its own last-month default. Only valid ranges are recorded (mid-edit states stay local).
  useEffect(() => {
    if (scope.startDate != null && scope.endDate != null && scope.startDate < scope.endDate) {
      setOverviewDateScope(classroomId, {
        dateMode: scope.dateMode,
        startDate: scope.startDate,
        endDate: scope.endDate
      });
    }
  }, [classroomId, scope.dateMode, scope.startDate, scope.endDate]);

  // Fetch students only once the classroom + its subjects are known — firing students_v2 with an
  // empty subject filter is a 400, and mega/no-subject classes don't render a roster.
  useEffect(() => {
    if (!needsStudents) {
      return undefined;
    }
    let isStale = false;
    studentsQuery
      .getResourcePromise()
      .then(() => {
        if (!isStale) {
          setLoadedKey(queryKey);
        }
      })
      .catch(() => {
        if (!isStale) {
          setFailedKey(queryKey);
        }
      });
    return () => {
      isStale = true;
    };
  }, [studentsQuery, queryKey, needsStudents]);

  const studentsReady = loadedKey === queryKey;
  const studentsFailed = failedKey === queryKey;

  // Bridged for the same compiler-freeze reason as the classroom read above.
  const studentsResource = useFluxStore(sessionStore, () =>
    studentsQuery.isResourcePopulated() ? studentsQuery.getResource() : null
  );
  const students =
    needsStudents && studentsReady && studentsResource
      ? (studentsResource as unknown as StudentsListCollection)
      : null;

  const tiles = useMemo<OverviewTiles>(() => {
    if (!students) {
      return EMPTY_TILES;
    }
    return {
      activeStudents: students.getActiveStudentsCount(),
      inactiveStudents: students.getInactiveStudentsCount(),
      questionsAnswered: students.getTotalNumberOfAttempts(),
      pointsEarned: students.getTotalPointsEarned(),
      averageAccuracy: students.getActiveStudentsAverageAccuracy(),
      timeSpentMs: students.getTotalTimeElapsed()
    };
  }, [students]);

  const classroomDone = classroomStatus !== 'loading';
  const studentsDone = !needsStudents || studentsReady || studentsFailed;

  return {
    isReady: classroomDone && studentsDone,
    classroomReady: classroomStatus === 'ready',
    isLoadingStudents: needsStudents && !studentsReady && !studentsFailed,
    hasError: classroomStatus === 'error' || (needsStudents && studentsFailed),
    isMegaClass,
    hasNoSubjects,
    students,
    tiles,
    subjectOptions,
    scope,
    setSubjectIds: (subjectIds) => setScope((prev) => ({...prev, subjectIds})),
    setAttemptNumber: (attemptNumber) => setScope((prev) => ({...prev, attemptNumber})),
    setDateRange: (startDate, endDate) => setScope((prev) => ({...prev, startDate, endDate})),
    setDateMode: (dateMode) => setScope((prev) => ({...prev, dateMode})),
    reload: () => {
      setClassroomStatus('loading');
      setLoadedKey(null);
      setFailedKey(null);
      classroomQuery.invalidateInterest();
      classroomQuery
        .getResourcePromise()
        .then(() => setClassroomStatus('ready'))
        .catch(() => setClassroomStatus('error'));
      if (needsStudents) {
        studentsQuery.invalidateInterest();
        studentsQuery
          .getResourcePromise()
          .then(() => setLoadedKey(queryKey))
          .catch(() => setFailedKey(queryKey));
      }
    }
  };
};
